import React, { useState } from "react";
import { Button, Card, Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PackageSearchSelectListItem } from "src/components/PackageSearchSelectListItem";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { ReactComponent as InfoCircleSVG } from "go-component/images/svg/info-circle.svg";
import { FormDirty } from "go-form/components/FormDirty";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { ChannelApi, LinkEntityApi, LinkEntityFormProps } from "../../../services/Api/Organization/types";
import { api } from "../../../services/Api/api";
import CreateContactModalForm from "../EntityForms/CreateContactModalForm";
import CreateProductModalForm from "../EntityForms/CreateProductModalForm";

const TranslationsInitialized = () => {
	const { t } = useTranslation();
	t("enums.apps.resource_type.PRODUCT");
	t("enums.apps.resource_type.CONTACT");
	t("enums.apps.resource_type.STOCK");
	t("enums.apps.resource_type.PRODUCT_CATEGORY");
};

interface Props {
	onHide: () => void;
	onSuccess: (response: any, volume?: number) => void;
	isShown: boolean;
	item: LinkEntityApi;
	channel?: ChannelApi;
}

const selectedOptionEnum = {
	link: "LINK",
	create: "CREATE",
	skip: "SKIP",
};

const LinkEntityModal = ({ isShown, onHide, onSuccess, item, channel }: Props) => {
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const form = useForm<LinkEntityFormProps>({
		criteriaMode: "all",
	});
	const {
		handleSubmit,
		formState,
		formState: { errors },
		control,
	} = form;
	const [selectedOption, setSelectedOption] = useState(selectedOptionEnum.link);
	const [productTypeToCreate, setProductTypeToCreate] = useState<"BASIC" | "PACKAGE">("BASIC");
	const [showCreateEntityModal, setShowCreateEntityModal] = useState<"PACKAGE" | "BASIC" | "CONTACT" | undefined>(
		undefined
	);
	const [loading, setLoading] = useState<boolean>(false);

	const isProduct = () => item.resource_type === "PRODUCT";
	const isStock = () => item.resource_type === "STOCK";
	const isContact = () => item.resource_type === "CONTACT";
	const isProductCategory = () => item.resource_type === "PRODUCT_CATEGORY";

	const getChannelName = () => {
		if (item?.integration_channel?.name) return item?.integration_channel?.name;
		if (channel?.name) return channel?.name;
		return "";
	};

	const onSubmit = handleSubmit(async (data) => {
		let res;
		setLoading(true);
		try {
			switch (selectedOption) {
				case selectedOptionEnum.link:
					if (!item.id && item.external_name) {
						data.item_name = item.external_name;
						if (isProduct()) res = await api.organization().linkProduct(data);
						if (isContact()) res = await api.organization().linkContact(data);
					} else {
						res = await api.organization().linkIntegrationLink(item.id, data);
					}
					onSuccess(Array.isArray(res) ? res[0] : res, data?.volume);
					addSuccessFlash(t("lib:common.flash.completed"));
					onHide();
					break;
				case selectedOptionEnum.create:
					if (isProduct()) setShowCreateEntityModal(productTypeToCreate);
					if (isContact()) setShowCreateEntityModal("CONTACT");
					break;
				case selectedOptionEnum.skip:
					if (!item.id && item.external_name) {
						data.item_name = item.external_name;
						if (isProduct()) res = await api.organization().skipProduct(data);
						if (isContact()) res = await api.organization().skipContact(data);
					} else {
						res = await api.organization().skipIntegrationLink(item.id);
					}
					onSuccess(Array.isArray(res) ? res[0] : res);
					addSuccessFlash(t("lib:common.flash.completed"));
					onHide();
					break;
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	});

	const onSearchResource = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		switch (item.resource_type) {
			case "PRODUCT":
				return api.organization().getProductSearchSelect(t, obj, {
					cancelToken: options?.token,
				});
			case "STOCK":
				return api.organization().getStockSearchSelect(t, obj, {
					cancelToken: options?.token,
				});
			case "CONTACT":
				return api.organization().getContactSearchSelect(t, obj, {
					cancelToken: options?.token,
				});
			case "PRODUCT_CATEGORY":
				return api.organization().getProductCategorySearchSelect(t, obj, {
					cancelToken: options?.token,
				});
		}
	};

	const drawProductForm = () => (
		<>
			<div className="d-flex align-items-center mb-1">
				<Form.Check
					type="radio"
					id="link-option"
					className="form-check"
					checked={selectedOption === selectedOptionEnum.link}
					onChange={() => setSelectedOption(selectedOptionEnum.link)}
				/>
				<Form.Check.Label htmlFor="link-option">
					<h6 className="mb-0">{t("modules.app_link.field.link_with_product.title")}</h6>
				</Form.Check.Label>
			</div>
			{selectedOption === selectedOptionEnum.link && (
				<>
					<FormSelectGroup
						name="resource_id"
						errors={errors}
						getOptionLabel={(option) => {
							return option.product_type === "PACKAGE" && item.resource_type === "PRODUCT" ? (
								<PackageSearchSelectListItem label={option.label} />
							) : (
								option.label
							);
						}}
						getOptionValue={(option) => option.id}
						defaultValue={{
							id: item?.resource_id,
							label: item?.resource_name,
						}}
						loadOptions={onSearchResource}
						control={control}
						data-testid="resource_id"
					/>
					{item.id && (
						<>
							<Form.Label>
								{t("common.word.volume")}
								<InfoCircleSVG className="ms-1" title={t("modules.app_link.field.volume.tooltip")} />
							</Form.Label>
							<FormNumberInput
								errors={errors}
								name="volume"
								control={control}
								suffix=""
								decimalScale={5}
							/>
						</>
					)}
				</>
			)}
			<hr />
			<div>
				<div className="d-flex align-items-center mb-1">
					<Form.Check
						type="radio"
						id="create-option"
						checked={selectedOption === selectedOptionEnum.create}
						className="form-check"
						onChange={() => setSelectedOption(selectedOptionEnum.create)}
					/>
					<Form.Check.Label htmlFor="create-option">
						<h6 className="mb-0">{t("modules.app_link.field.create_new_product.title")}</h6>
					</Form.Check.Label>
				</div>
				{selectedOption === selectedOptionEnum.create && (
					<div className="p-3">
						<div className="d-flex align-items-center mb-1">
							<Form.Check
								type="radio"
								id="basic-option"
								className="form-check"
								checked={productTypeToCreate === "BASIC"}
								onChange={() => setProductTypeToCreate("BASIC")}
							/>
							<Form.Check.Label htmlFor="basic-option">
								<h6 className="mb-0">{t("modules.app_link.field.basic_product.title")}</h6>
							</Form.Check.Label>
						</div>
						<div className="d-flex align-items-center mb-1">
							<Form.Check
								type="radio"
								id="package-option"
								className="form-check"
								checked={productTypeToCreate === "PACKAGE"}
								onChange={() => setProductTypeToCreate("PACKAGE")}
							/>
							<Form.Check.Label htmlFor="package-option">
								<h6 className="mb-0">{t("modules.app_link.field.package.title")}</h6>
							</Form.Check.Label>
						</div>
					</div>
				)}
			</div>

			<hr />
			<div className="d-flex align-items-center mb-1">
				<Form.Check
					type="radio"
					id="skip-option"
					className="form-check"
					checked={selectedOption === selectedOptionEnum.skip}
					onChange={() => setSelectedOption(selectedOptionEnum.skip)}
				/>
				<Form.Check.Label htmlFor="skip-option">
					<h6 className="mb-0">{t("modules.app_link.field.not_use_product.title")}</h6>
				</Form.Check.Label>
			</div>
		</>
	);

	const drawStockForm = () => (
		<>
			<div className="d-flex align-items-center mb-1">
				<Form.Check
					type="radio"
					id="link-option"
					className="form-check"
					checked={selectedOption === selectedOptionEnum.link}
					onChange={() => setSelectedOption(selectedOptionEnum.link)}
				/>
				<Form.Check.Label htmlFor="link-option">
					<h6 className="mb-0">{t("modules.app_link.field.link_with_stock.title")}</h6>
				</Form.Check.Label>
			</div>
			{selectedOption !== selectedOptionEnum.skip && (
				<FormSelectGroup
					name="resource_id"
					errors={errors}
					getOptionLabel={(option) => {
						return option.product_type === "PACKAGE" && item.resource_type === "PRODUCT" ? (
							<PackageSearchSelectListItem label={option.label} />
						) : (
							option.label
						);
					}}
					getOptionValue={(option) => option.id}
					defaultValue={{
						id: item?.resource_id,
						label: item?.resource_name,
					}}
					loadOptions={onSearchResource}
					control={control}
					data-testid="resource_id"
				/>
			)}
			<hr />
			<div className="d-flex align-items-center mb-1">
				<Form.Check
					type="radio"
					id="skip-option"
					className="form-check"
					checked={selectedOption === selectedOptionEnum.skip}
					onChange={() => setSelectedOption(selectedOptionEnum.skip)}
				/>
				<Form.Check.Label htmlFor="skip-option">
					<h6 className="mb-0">{t("modules.app_link.field.not_use_stock.title")}</h6>
				</Form.Check.Label>
			</div>
		</>
	);
	const drawContactForm = () => (
		<>
			<div className="d-flex align-items-center mb-1">
				<Form.Check
					type="radio"
					id="link-option"
					className="form-check"
					checked={selectedOption === selectedOptionEnum.link}
					onChange={() => setSelectedOption(selectedOptionEnum.link)}
				/>
				<Form.Check.Label htmlFor="link-option">
					<h6 className="mb-0">{t("modules.app_link.field.link_with_contact.title")}</h6>
				</Form.Check.Label>
			</div>
			<FormSelectGroup
				name="resource_id"
				errors={errors}
				getOptionLabel={(option) => {
					return option.product_type === "PACKAGE" && item.resource_type === "PRODUCT" ? (
						<PackageSearchSelectListItem label={option.label} />
					) : (
						option.label
					);
				}}
				getOptionValue={(option) => option.id}
				defaultValue={{
					id: item?.resource_id,
					label: item?.resource_name,
				}}
				loadOptions={onSearchResource}
				control={control}
				data-testid="resource_id"
			/>
			<hr />
			<div>
				<div className="d-flex align-items-center mb-1">
					<Form.Check
						type="radio"
						id="create-option"
						className="form-check"
						checked={selectedOption === selectedOptionEnum.create}
						onChange={() => setSelectedOption(selectedOptionEnum.create)}
					/>
					<Form.Check.Label htmlFor="create-option">
						<h6 className="mb-0">{t("modules.app_link.field.create_new_contact.title")}</h6>
					</Form.Check.Label>
				</div>
			</div>

			<hr />
			<div className="d-flex align-items-center mb-1">
				<Form.Check
					type="radio"
					checked={selectedOption === selectedOptionEnum.skip}
					id="skip-option"
					className="form-check"
					onChange={() => setSelectedOption(selectedOptionEnum.skip)}
				/>
				<Form.Check.Label htmlFor="skip-option">
					<h6 className="mb-0">{t("modules.app_link.field.not_use_contact.title")}</h6>
				</Form.Check.Label>
			</div>
		</>
	);
	const drawProductCategoryForm = () => (
		<>
			<div className="d-flex align-items-center mb-1">
				<Form.Check
					type="radio"
					id="link-option"
					className="form-check"
					checked={selectedOption === selectedOptionEnum.link}
					onChange={() => setSelectedOption(selectedOptionEnum.link)}
				/>
				<Form.Check.Label htmlFor="link-option">
					<h6 className="mb-0">{t("modules.app_link.field.link_with_product_category.title")}</h6>
				</Form.Check.Label>
			</div>
			<FormSelectGroup
				name="resource_id"
				errors={errors}
				getOptionLabel={(option) => {
					return option.product_type === "PACKAGE" && item.resource_type === "PRODUCT" ? (
						<PackageSearchSelectListItem label={option.label} />
					) : (
						option.label
					);
				}}
				getOptionValue={(option) => option.id}
				defaultValue={{
					id: item?.resource_id,
					label: item?.resource_name,
				}}
				loadOptions={onSearchResource}
				control={control}
				data-testid="resource_id"
			/>
			<hr />
			<div className="d-flex align-items-center mb-1">
				<Form.Check
					type="radio"
					id="skip-option"
					className="form-check"
					checked={selectedOption === selectedOptionEnum.skip}
					onChange={() => setSelectedOption(selectedOptionEnum.skip)}
				/>
				<Form.Check.Label htmlFor="skip-option">
					<h6 className="mb-0">{t("modules.app_link.field.not_use_product_category.title")}</h6>
				</Form.Check.Label>
			</div>
		</>
	);

	return (
		<>
			<Modal onHide={onHide} show={isShown} size="lg" className={`${showCreateEntityModal ? "d-none" : ""}`}>
				<FormDirty key="link-entity-modal" noValidate onSubmit={onSubmit} formState={formState}>
					<Modal.Header closeButton>
						<Modal.Title>
							{t("common.action.connect")}{" "}
							{t(`enums.apps.resource_type.${item.resource_type}`).toLowerCase()}
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<small className="text-muted">
							{t("modules.app_link.field.connection_description.title", {
								resource: t(`enums.apps.resource_type.${item.resource_type}`).toLowerCase(),
							})}
						</small>
						<div className="row mt-3">
							<div className="col-md-6">
								<h5>
									{t(`enums.apps.resource_type.${item.resource_type}`)} {t("lib:common.word.of")}{" "}
									{getChannelName()}
								</h5>
								<Card>
									<Card.Body>
										<h6 className="mb-0">{item?.external_name}</h6>
									</Card.Body>
								</Card>
							</div>
							<div className="col-md-6">
								<h5>
									{t("modules.app_link.field.resource_existence.title", {
										resource: t(`enums.apps.resource_type.${item.resource_type}`).toLowerCase(),
									})}
								</h5>
								<Card>
									<Card.Body>
										{isProduct() && drawProductForm()}
										{isStock() && drawStockForm()}
										{isContact() && drawContactForm()}
										{isProductCategory() && drawProductCategoryForm()}
									</Card.Body>
								</Card>
							</div>
						</div>
					</Modal.Body>
					<Modal.Footer>
						<ButtonLoading type="submit" onClick={onSubmit} loading={loading}>
							{selectedOption === selectedOptionEnum.create
								? t("modules.app_link.action.next.title")
								: t("lib:common.action.save")}
						</ButtonLoading>
						<Button variant="light" onClick={onHide}>
							{t("lib:common.action.cancel")}
						</Button>
					</Modal.Footer>
				</FormDirty>
			</Modal>
			{(showCreateEntityModal === "BASIC" || showCreateEntityModal === "PACKAGE") && (
				<CreateProductModalForm
					isShown={!!showCreateEntityModal}
					onSuccess={onSuccess}
					onHide={() => {
						setTimeout(() => {
							onHide();
						}, 10);
					}}
					linkedId={item.id}
					onGoBack={() => setShowCreateEntityModal(undefined)}
					product={{
						type: showCreateEntityModal,
						name: item?.external_name,
						tax: item?.tax,
						sku: item?.sku,
					}}
				/>
			)}
			{showCreateEntityModal === "CONTACT" && (
				<CreateContactModalForm
					isShown={!!showCreateEntityModal}
					onHide={() => {
						setTimeout(() => {
							onHide();
						}, 10);
					}}
					onSuccess={onSuccess}
					linkedId={item.id}
					name={item?.external_name}
					onGoBack={() => setShowCreateEntityModal(undefined)}
				/>
			)}
		</>
	);
};

export default LinkEntityModal;
